import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Box,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Paper,
  TablePagination,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import Tooltip from '@mui/material/Tooltip';
import { ACCOUNTING_API } from '../../../config/api';
import { accountingApi } from '../../../config/apiClient';
import { AppDatePicker } from '../../common/AppDatePicker';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import ProjectSelector from '../shared/ProjectSelector';
import ExpenseCategorySelect from '../cashbox/ExpenseCategorySelect';
import GuideButton from '../../common/guide/GuideButton';
import { parseTrAmount } from '../../../utils/numberFormat';

export interface CreditCardForAction {
  id: number;
  accountName: string;
  cardNumber: string;
  creditLimit: number;
  usedAmount: number;
  currency: string;
}

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const toFinalDate = (dateStr: string): string => {
  const selected = new Date(dateStr);
  const now = new Date();
  const isToday =
    selected.getFullYear() === now.getFullYear() &&
    selected.getMonth() === now.getMonth() &&
    selected.getDate() === now.getDate();
  const finalDate = isToday
    ? new Date()
    : new Date(selected.getFullYear(), selected.getMonth(), selected.getDate(), 12, 0, 0);
  return finalDate.toISOString();
};

// ===================== Harcama Ekle (Add Usage) =====================
interface UsageModalProps {
  open: boolean;
  onClose: () => void;
  card: CreditCardForAction | null;
  onSuccess?: () => void;
  /** Şube yöneticisi: gider türü zorunlu, proje seçimi gizli */
  isManager?: boolean;
}

export const CreditCardUsageModal: React.FC<UsageModalProps> = ({ open, onClose, card, onSuccess, isManager = false }) => {
  const { userDetails } = useAuth();
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (tr: string, en: string) => (isTr ? tr : en);
  const localeCode = isTr ? 'tr-TR' : 'en-US';

  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [documentNo, setDocumentNo] = useState('');
  const [date, setDate] = useState(todayStr());
  // PYS: kart harcamasını bir projeye bağlar (proje maliyetine yazılır)
  const [projectId, setProjectId] = useState<number | null>(null);
  const [expenseCategoryId, setExpenseCategoryId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setAmount('');
      setDescription('');
      setDocumentNo('');
      setDate(todayStr());
      setProjectId(null);
      setExpenseCategoryId(null);
      setError(null);
    }
  }, [open]);

  if (!card) return null;

  const available = card.creditLimit - card.usedAmount;

  const handleSave = async () => {
    const amountValue = parseTrAmount(amount);
    if (!amountValue || amountValue <= 0) {
      setError(tt('Geçerli bir tutar giriniz.', 'Please enter a valid amount.'));
      return;
    }
    if (amountValue > available) {
      setError(tt(`Kart limiti yetersiz. Kalan limit: ${available.toLocaleString(localeCode)} ${card.currency}`,
        `Insufficient card limit. Available: ${available.toLocaleString(localeCode)} ${card.currency}`));
      return;
    }
    if (isManager && !expenseCategoryId) {
      setError(tt('Lütfen gider türü seçiniz.', 'Please select an expense type.'));
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await accountingApi.post(ACCOUNTING_API.CREDIT_CARD_ADD_USAGE(card.id), {
        Amount: amountValue,
        Description: description || tt('Kart harcaması', 'Card usage'),
        DocumentNo: documentNo.trim() || null,
        Date: toFinalDate(date),
        ProjectId: projectId,
        ExpenseCategoryId: expenseCategoryId,
        UpdatedBy: userDetails?.email || 'Sistem',
      });
      setLoading(false);
      onSuccess?.();
      onClose();
    } catch (err: any) {
      setLoading(false);
      setError(err?.response?.data?.message || err.message || tt('Harcama eklenemedi', 'Could not add usage'));
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography variant="h6">{tt('Harcama Ekle', 'Add Usage')}</Typography>
            <GuideButton guideKey="finance.creditCardExpense" size="small" />
          </Box>
          <IconButton edge="end" onClick={onClose} disabled={loading}><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
          <Typography variant="subtitle2" color="textSecondary">{card.accountName} {card.cardNumber ? `(${card.cardNumber})` : ''}</Typography>
          <Typography variant="body2">{tt('Kalan Limit', 'Available Limit')}: <b>{available.toLocaleString(localeCode)} {card.currency}</b></Typography>
        </Paper>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField label={tt('Tutar *', 'Amount *')} value={amount} onChange={(e) => setAmount(e.target.value)} fullWidth sx={{ mb: 2 }} disabled={loading} />
        <AppDatePicker label={tt('İşlem Tarihi *', 'Transaction Date *')} value={date} onChange={setDate} fullWidth size="medium" sx={{ mb: 2 }} disabled={loading} />
        <TextField label={tt('Belge No', 'Document No')} value={documentNo} onChange={(e) => setDocumentNo(e.target.value)} fullWidth sx={{ mb: 2 }} disabled={loading} />
        <ExpenseCategorySelect
          value={expenseCategoryId}
          onChange={setExpenseCategoryId}
          label={isManager ? tt('Gider türü *', 'Expense type *') : tt('Gider türü (opsiyonel)', 'Expense type (optional)')}
          helperText={isManager
            ? tt('Harcama Kâr-Zarar\'da bu gider türü altında görünür', 'The usage is shown under this expense type in P&L')
            : tt('Boş bırakılırsa Kâr-Zarar\'da "Kategorisiz" görünür', 'If empty, shown as "Uncategorized" in P&L')}
          size="medium"
          disabled={loading}
          sx={{ mb: 2 }}
        />
        {!isManager && (
          <Box sx={{ mb: 2 }}>
            <ProjectSelector value={projectId} onChange={setProjectId} disabled={loading} />
          </Box>
        )}
        <TextField label={tt('Açıklama', 'Description')} value={description} onChange={(e) => setDescription(e.target.value)} fullWidth multiline rows={2} disabled={loading} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>{tt('İptal', 'Cancel')}</Button>
        <Button variant="contained" onClick={handleSave} disabled={loading} startIcon={loading ? <CircularProgress size={16} /> : undefined}>{tt('Kaydet', 'Save')}</Button>
      </DialogActions>
    </Dialog>
  );
};

// ===================== Ödeme Yap (Make Payment) =====================
interface PaymentModalProps {
  open: boolean;
  onClose: () => void;
  card: CreditCardForAction | null;
  onSuccess?: () => void;
  /** Şube yöneticisi: kaynak yalnız bu şubenin kasası/banka hesabı olabilir; tahakkuk ve proje kapalı */
  managerBranchId?: string | null;
}

export const CreditCardPaymentModal: React.FC<PaymentModalProps> = ({ open, onClose, card, onSuccess, managerBranchId = null }) => {
  const isManager = !!managerBranchId;
  const { userDetails } = useAuth();
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (tr: string, en: string) => (isTr ? tr : en);
  const localeCode = isTr ? 'tr-TR' : 'en-US';

  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState(todayStr());
  const [source, setSource] = useState<'cashbox' | 'bank' | 'accrual'>('cashbox');
  const [cashboxes, setCashboxes] = useState<any[]>([]);
  const [bankAccounts, setBankAccounts] = useState<any[]>([]);
  const [selectedCashboxId, setSelectedCashboxId] = useState<string>('');
  const [selectedBankId, setSelectedBankId] = useState<string>('');
  // PYS: ödemeyi bir projeye bağlar (kasa/banka nakit çıkışına da taşınır)
  const [projectId, setProjectId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && card) {
      setAmount('');
      setDescription('');
      setDate(todayStr());
      setSource('cashbox');
      setSelectedCashboxId('');
      setSelectedBankId('');
      setProjectId(null);
      setError(null);
      loadSources();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, card]);

  const loadSources = async () => {
    try {
      const [cbRes, bankRes] = await Promise.all([
        accountingApi.get(ACCOUNTING_API.CASHBOX_BY_COMPANY),
        accountingApi.get(ACCOUNTING_API.BANK_ACCOUNT_BY_COMPANY),
      ]);
      const cbData = cbRes.data?.data || cbRes.data || [];
      const bankData = bankRes.data?.data || bankRes.data || [];
      const cur = card?.currency || 'TRY';
      const inBranch = (x: any) => !managerBranchId || x.branchId === managerBranchId;
      setCashboxes(cbData.filter((c: any) => (c.currency || 'TRY') === cur && inBranch(c)));
      setBankAccounts(bankData.filter((b: any) => (b.currency || 'TRY') === cur && inBranch(b)));
    } catch (err) {
      console.error('[CreditCardPaymentModal] Kaynaklar yüklenemedi:', err);
    }
  };

  if (!card) return null;

  const handleSave = async () => {
    const amountValue = parseTrAmount(amount);
    if (!amountValue || amountValue <= 0) {
      setError(tt('Geçerli bir tutar giriniz.', 'Please enter a valid amount.'));
      return;
    }
    if (amountValue > card.usedAmount) {
      setError(tt(`Ödeme tutarı borçtan fazla olamaz. Güncel borç: ${card.usedAmount.toLocaleString(localeCode)} ${card.currency}`,
        `Payment cannot exceed debt. Current debt: ${card.usedAmount.toLocaleString(localeCode)} ${card.currency}`));
      return;
    }
    if (source === 'cashbox' && !selectedCashboxId) {
      setError(tt('Lütfen bir kasa seçiniz.', 'Please select a cashbox.'));
      return;
    }
    if (source === 'bank' && !selectedBankId) {
      setError(tt('Lütfen bir banka hesabı seçiniz.', 'Please select a bank account.'));
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await accountingApi.post(ACCOUNTING_API.CREDIT_CARD_PAYMENT(card.id), {
        Amount: amountValue,
        Description: description || tt('Kredi kartı borç ödemesi', 'Credit card debt payment'),
        Date: toFinalDate(date),
        PaymentSource: source,
        CashboxId: source === 'cashbox' ? selectedCashboxId : null,
        BankAccountId: source === 'bank' ? selectedBankId : null,
        ProjectId: projectId,
        UpdatedBy: userDetails?.email || 'Sistem',
      });
      setLoading(false);
      onSuccess?.();
      onClose();
    } catch (err: any) {
      setLoading(false);
      setError(err?.response?.data?.message || err.message || tt('Ödeme yapılamadı', 'Could not make payment'));
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography variant="h6">{tt('Ödeme Yap', 'Make Payment')}</Typography>
            <GuideButton guideKey="finance.creditCardPayment" size="small" />
          </Box>
          <IconButton edge="end" onClick={onClose} disabled={loading}><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
          <Typography variant="subtitle2" color="textSecondary">{card.accountName} {card.cardNumber ? `(${card.cardNumber})` : ''}</Typography>
          <Typography variant="body2" color="error.main">{tt('Güncel Borç', 'Current Debt')}: <b>{card.usedAmount.toLocaleString(localeCode)} {card.currency}</b></Typography>
        </Paper>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField label={tt('Tutar *', 'Amount *')} value={amount} onChange={(e) => setAmount(e.target.value)} fullWidth sx={{ mb: 2 }} disabled={loading} />

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel id="cc-source-label">{tt('Ödeme Kaynağı *', 'Payment Source *')}</InputLabel>
          <Select labelId="cc-source-label" value={source} label={tt('Ödeme Kaynağı *', 'Payment Source *')} onChange={(e) => setSource(e.target.value as any)} disabled={loading}>
            <MenuItem value="cashbox">{tt('Kasa', 'Cashbox')}</MenuItem>
            <MenuItem value="bank">{tt('Banka', 'Bank')}</MenuItem>
            {!isManager && <MenuItem value="accrual">{tt('Tahakkuk (Elden)', 'Accrual (By hand)')}</MenuItem>}
          </Select>
        </FormControl>

        {source === 'cashbox' && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="cc-cashbox-label">{tt('Kasa Seçin *', 'Select Cashbox *')}</InputLabel>
            <Select labelId="cc-cashbox-label" value={selectedCashboxId} label={tt('Kasa Seçin *', 'Select Cashbox *')} onChange={(e) => setSelectedCashboxId(String(e.target.value))} disabled={loading || cashboxes.length === 0}>
              {cashboxes.length === 0 ? (
                <MenuItem disabled value="">{tt('Uygun kasa bulunamadı', 'No suitable cashbox found')}</MenuItem>
              ) : cashboxes.map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>{c.name} ({tt('Bakiye', 'Balance')}: {Number(c.balance || 0).toLocaleString(localeCode)} {c.currency})</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {source === 'bank' && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="cc-bank-label">{tt('Banka Hesabı Seçin *', 'Select Bank Account *')}</InputLabel>
            <Select labelId="cc-bank-label" value={selectedBankId} label={tt('Banka Hesabı Seçin *', 'Select Bank Account *')} onChange={(e) => setSelectedBankId(String(e.target.value))} disabled={loading || bankAccounts.length === 0}>
              {bankAccounts.length === 0 ? (
                <MenuItem disabled value="">{tt('Uygun banka hesabı bulunamadı', 'No suitable bank account found')}</MenuItem>
              ) : bankAccounts.map((b) => (
                <MenuItem key={b.id} value={String(b.id)}>{b.bankName} ({tt('Bakiye', 'Balance')}: {Number(b.balance || 0).toLocaleString(localeCode)} {b.currency})</MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {source === 'accrual' && (
          <Alert severity="info" sx={{ mb: 2 }}>{tt('Tahakkuk (elden) seçildiğinde kasa/banka bakiyesi değişmez; sadece kart borcu düşer.', 'When accrual (by hand) is selected, no cashbox/bank balance changes; only the card debt is reduced.')}</Alert>
        )}

        <AppDatePicker label={tt('İşlem Tarihi *', 'Transaction Date *')} value={date} onChange={setDate} fullWidth size="medium" sx={{ mb: 2 }} disabled={loading} />
        {!isManager && (
          <>
            <Box sx={{ mb: 0.5 }}>
              <ProjectSelector value={projectId} onChange={setProjectId} disabled={loading} />
            </Box>
            <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
              {tt('Borç ödemesi projede nakit çıkışı olarak görünür; maliyet kart harcamasında sayılır.',
                'A debt payment appears as cash out in the project; the cost is counted on the card usage.')}
            </Typography>
          </>
        )}
        <TextField label={tt('Açıklama', 'Description')} value={description} onChange={(e) => setDescription(e.target.value)} fullWidth multiline rows={2} disabled={loading} />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>{tt('İptal', 'Cancel')}</Button>
        <Button variant="contained" color="success" onClick={handleSave} disabled={loading} startIcon={loading ? <CircularProgress size={16} /> : undefined}>{tt('Ödeme Yap', 'Make Payment')}</Button>
      </DialogActions>
    </Dialog>
  );
};

// ===================== İşlem Geçmişi (Transactions) =====================
interface TransactionsModalProps {
  open: boolean;
  onClose: () => void;
  card: CreditCardForAction | null;
  /** Hareket silindiğinde kart listesinin yenilenmesi için */
  onChanged?: () => void;
  /** Şube yöneticisinde false: hareket silme kapalı */
  canDelete?: boolean;
}

export const CreditCardTransactionsModal: React.FC<TransactionsModalProps> = ({ open, onClose, card, onChanged, canDelete = true }) => {
  const { userDetails } = useAuth();
  const { i18n } = useTranslation();
  const isTr = i18n.language.startsWith('tr');
  const tt = (tr: string, en: string) => (isTr ? tr : en);
  const localeCode = isTr ? 'tr-TR' : 'en-US';

  const [transactions, setTransactions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [txToDelete, setTxToDelete] = useState<any | null>(null);
  const [deleting, setDeleting] = useState(false);
  // Server-side sayfalama: binlerce hareket olan kartlarda tüm listeyi tek
  // seferde çekmek yerine yalnızca görünen sayfa yüklenir.
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  useEffect(() => {
    if (open && card) {
      setTxToDelete(null);
      setPage(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, card]);

  useEffect(() => {
    if (open && card) {
      loadTransactions(card.id, page, pageSize);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, card, page, pageSize]);

  const loadTransactions = async (id: number, pageIdx: number, size: number) => {
    try {
      setLoading(true);
      setError(null);
      // Backend 1 tabanlı sayfa bekler; TablePagination 0 tabanlıdır.
      const res = await accountingApi.get(ACCOUNTING_API.CREDIT_CARD_TRANSACTIONS(id, { page: pageIdx + 1, pageSize: size }));
      setTransactions(res.data?.data || res.data || []);
      setTotalCount(res.data?.totalCount ?? (res.data?.data?.length || 0));
      setLoading(false);
    } catch (err: any) {
      setLoading(false);
      setError(err?.response?.data?.message || err.message || tt('Hareketler yüklenemedi', 'Could not load transactions'));
    }
  };

  const handleConfirmDeleteTx = async () => {
    if (!card || !txToDelete) return;
    try {
      setDeleting(true);
      setError(null);
      await accountingApi.post(
        ACCOUNTING_API.CREDIT_CARD_TRANSACTION_DELETE(card.id, txToDelete.id),
        {},
        { params: { deletedBy: userDetails?.email || 'Sistem' } }
      );
      setTxToDelete(null);
      await loadTransactions(card.id, page, pageSize);
      onChanged?.();
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || tt('Hareket silinemedi', 'Could not delete transaction'));
      setTxToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  // Cariye yapılan kredi kartı ödemeleri cari harekete bağlıdır; buradan silinemez
  const isAccountLinked = (tx: any) => (tx?.referenceType || '').toUpperCase() === 'ACCOUNT_PAYMENT';

  if (!card) return null;

  const formatDate = (d: string) => {
    try { return new Date(d).toLocaleDateString(localeCode); } catch { return d; }
  };

  const typeChip = (type: string) => {
    if (type === 'USAGE') return <Chip size="small" color="warning" label={tt('Harcama', 'Usage')} />;
    if (type === 'PAYMENT') return <Chip size="small" color="success" label={tt('Ödeme', 'Payment')} />;
    return <Chip size="small" label={type} />;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography variant="h6">{tt('Kart Hareketleri', 'Card Transactions')} — {card.accountName}</Typography>
            <GuideButton guideKey="finance.creditCardTransactions" size="small" />
          </Box>
          <IconButton edge="end" onClick={onClose}><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
          <Typography variant="body2">{tt('Limit', 'Limit')}: <b>{card.creditLimit.toLocaleString(localeCode)} {card.currency}</b> • {tt('Kullanılan', 'Used')}: <b>{card.usedAmount.toLocaleString(localeCode)} {card.currency}</b> • {tt('Kalan', 'Available')}: <b>{(card.creditLimit - card.usedAmount).toLocaleString(localeCode)} {card.currency}</b></Typography>
        </Paper>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? (
          <Box textAlign="center" p={3}><CircularProgress /></Box>
        ) : transactions.length === 0 ? (
          <Typography color="textSecondary" textAlign="center" sx={{ py: 3 }}>{tt('Henüz hareket bulunmuyor.', 'No transactions yet.')}</Typography>
        ) : (
          <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{tt('Tarih', 'Date')}</TableCell>
                  <TableCell>{tt('Tip', 'Type')}</TableCell>
                  <TableCell>{tt('Açıklama', 'Description')}</TableCell>
                  <TableCell>{tt('Belge No', 'Doc No')}</TableCell>
                  <TableCell align="right">{tt('Tutar', 'Amount')}</TableCell>
                  <TableCell align="right">{tt('Sonraki Borç', 'Debt After')}</TableCell>
                  {canDelete && <TableCell align="center">{tt('İşlem', 'Action')}</TableCell>}
                </TableRow>
              </TableHead>
              <TableBody>
                {transactions.map((tx) => (
                  <TableRow key={tx.id}>
                    <TableCell>{formatDate(tx.date)}</TableCell>
                    <TableCell>{typeChip(tx.transactionType)}</TableCell>
                    <TableCell>
                      {tx.description || (tx.accountName ? `${tt('Cari', 'Account')}: ${tx.accountName}` : '-')}
                      {tx.projectName && (
                        <Chip size="small" variant="outlined" color="primary" label={tx.projectName} sx={{ ml: 1 }} />
                      )}
                    </TableCell>
                    <TableCell>{tx.documentNo || '-'}</TableCell>
                    <TableCell align="right" sx={{ color: tx.transactionType === 'PAYMENT' ? 'success.main' : 'warning.main' }}>
                      {tx.transactionType === 'PAYMENT' ? '-' : '+'}{Number(tx.amount || 0).toLocaleString(localeCode)} {tx.currency}
                    </TableCell>
                    <TableCell align="right">{Number(tx.usedAmountAfter || 0).toLocaleString(localeCode)} {tx.currency}</TableCell>
                    {canDelete && (
                    <TableCell align="center">
                      <Tooltip title={isAccountLinked(tx)
                        ? tt('Cariye bağlı hareket buradan silinemez. Lütfen cari hareketlerden siliniz.', 'Linked to an account transaction; delete it from account transactions.')
                        : tt('Hareketi sil (kart bakiyesi geri düzeltilir)', 'Delete transaction (card balance is restored)')}>
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={deleting || isAccountLinked(tx)}
                            onClick={() => setTxToDelete(tx)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(_e, p) => setPage(p)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => { setPageSize(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage={tt('Sayfa başına satır:', 'Rows per page:')}
            labelDisplayedRows={({ from, to, count }) => isTr ? `${count} kayıttan ${from}–${to} arası` : `${from}–${to} of ${count}`}
          />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{tt('Kapat', 'Close')}</Button>
      </DialogActions>

      {/* Hareket Silme Onayı */}
      <Dialog open={Boolean(txToDelete)} onClose={deleting ? undefined : () => setTxToDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{tt('Kart Hareketini Sil', 'Delete Card Transaction')}</DialogTitle>
        <DialogContent dividers>
          {txToDelete && (
            <>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {tt('Bu hareketi silmek istediğinize emin misiniz?', 'Are you sure you want to delete this transaction?')}
              </Typography>
              <Paper variant="outlined" sx={{ p: 1.5, mb: 1.5, bgcolor: 'background.default' }}>
                <Typography variant="body2">
                  <b>{txToDelete.transactionType === 'PAYMENT' ? tt('Ödeme', 'Payment') : tt('Harcama', 'Usage')}</b>
                  {' — '}{Number(txToDelete.amount || 0).toLocaleString(localeCode)} {txToDelete.currency}
                  {' — '}{formatDate(txToDelete.date)}
                </Typography>
                {txToDelete.description && (
                  <Typography variant="caption" color="textSecondary">{txToDelete.description}</Typography>
                )}
              </Paper>
              <Alert severity="warning">
                {txToDelete.transactionType === 'PAYMENT'
                  ? tt('Silme sonrası kart borcu geri eklenir; ödeme kasadan/bankadan yapıldıysa tutar kaynağa iade edilir.',
                      'After deletion the card debt is restored; if the payment was made from a cashbox/bank, the amount is refunded to the source.')
                  : tt('Silme sonrası kart borcu (kullanılan limit) bu tutar kadar azaltılır.',
                      'After deletion the card debt (used limit) is reduced by this amount.')}
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTxToDelete(null)} disabled={deleting}>{tt('İptal', 'Cancel')}</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDeleteTx}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleting ? tt('Siliniyor...', 'Deleting...') : tt('Sil', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};
